import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema, faqSchema, webSiteSchema, howToSetupSchema } from '../components/StructuredData';
import { whyTableChatPageSchema } from '../components/StructuredData';
import PhoneMockup from '../components/PhoneMockup';
import './Features.css';

const whyTableChatFAQs = [
  {
    question: 'How does TableChat compare to QR code ordering systems?',
    answer: 'QR code ordering systems replace the server with a digital menu. TableChat is the opposite — it keeps your staff central to the experience and gives guests a direct line to communicate with them. Guests tap an NFC tag (no camera, no QR scan) and enter a live chat. They can request refills, ask questions, or flag issues without waiting for a server to walk by. TableChat enhances service; it does not replace it.',
  },
  {
    question: 'Does TableChat require guests to download an app?',
    answer: 'No. TableChat uses NFC technology built into modern smartphones. When a guest taps the NFC tag at their table, a web-based chat opens directly in their browser. There is no app to download, no account to create, and no login required. It works on both iPhone and Android devices.',
  },
  {
    question: 'What makes TableChat different from other restaurant technology platforms?',
    answer: 'Most restaurant tech focuses on ordering, payments, or reservations. TableChat focuses on the in-meal experience — the 45 to 90 minutes a guest is seated. It provides real-time communication between guests and staff, automated promotions delivered at the table, VIP guest data capture with consent, and integrations with marketing tools like Klaviyo and Mailchimp. No other platform combines NFC-powered table-side messaging with built-in marketing automation.',
  },
  {
    question: 'Can TableChat work alongside my existing POS and reservation system?',
    answer: 'Yes. TableChat operates independently of your POS, reservation, and ordering systems. It does not replace any existing technology — it fills the communication gap between guest and staff that other systems do not address. TableChat works alongside Toast, Square, Clover, OpenTable, Resy, and other industry tools.',
  },
  {
    question: 'How much does TableChat cost for a restaurant?',
    answer: 'TableChat is $379 per month per restaurant location with an annual commitment. This includes unlimited staff accounts, all software features, NFC hardware, automated promotions, VIP data capture, marketing integrations, daily operational reports, and email support. There are no per-transaction fees, no per-message charges, and no hidden costs.',
  },
  {
    question: 'What kind of restaurants is TableChat designed for?',
    answer: 'TableChat is designed for full-service restaurants, fast-casual concepts with table seating, bars, breweries, and any hospitality venue where guests are seated at a table and might need to communicate with staff. It works for single-location independents and multi-unit operators alike.',
  },
];

const WhyTableChat: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="TableChat - The NFC Guest Engagement Platform for Restaurants"
        description="TableChat is an NFC-powered guest engagement platform that combines real-time table-side messaging, automated promotions, VIP loyalty capture, and marketing integrations — all without requiring guests to download an app. Built for restaurant owners who want to improve service, capture guest data, and drive repeat visits."
        canonical="https://tablechat.io/"
      />
      <StructuredData data={whyTableChatPageSchema} />
      <StructuredData data={webSiteSchema} />
      <StructuredData data={howToSetupSchema} />
      <StructuredData data={faqSchema(whyTableChatFAQs)} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Guest Engagement that Actively Grows your Business</h1>
              <p className="hero-lead">
                TableChat combines NFC-powered table-side messaging, automated promotions, VIP loyalty capture, 
                and third-party marketing integrations into a single platform. Guests tap to connect — no app 
                download, no QR scan, no friction. Restaurants capture data, drive upsells, and build direct 
                relationships with the people already in their seats.
              </p>
            </div>
            <div className="feature-hero-image">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>The Communication Gap Restaurants Can't Afford</h2>
            <p>
              Between when a guest sits down and when a server checks in, minutes pass. Requests go 
              unspoken. Specials go unmentioned. And when the guest leaves, the restaurant has no way 
              to reach them again.
            </p>
          </div>

          <div className="feature-blocks">
            {/* Real-Time Messaging */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Real-Time Table-Side Messaging</h3>
                <p>
                  Guests tap the NFC tag at their table and open a live chat with staff — instantly. 
                  No app download, no QR code scan, no login. They can request refills, ask about 
                  allergens, flag issues, or call for the check. Messages route to your staff kiosk 
                  in real time with table number context.
                </p>
                <p>
                  Staff see every active request organized by table, respond through the AI-powered 
                  chat assistant, and mark requests as handled. The system ensures nothing falls through 
                  the cracks during rush periods when servers are stretched thin.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Messaging Capabilities</h4>
                <ul>
                  <li>NFC tap-to-connect — works on iPhone and Android</li>
                  <li>No app download or account creation required</li>
                  <li>Real-time message routing to staff kiosk</li>
                  <li>AI-powered chat assistant handles guest requests</li>
                  <li>Multi-language automatic translation</li>
                  <li>Table number context on every message</li>
                </ul>
              </div>
            </div>

            {/* Automated Promotions */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Automated Promotions at the Table</h3>
                <p>
                  Every guest who taps in sees your welcome message, daily specials, and any active 
                  promotional offers — automatically. No server pitch required. No printed table tents 
                  to swap. Update specials from the management console in seconds and every new guest 
                  sees the latest version.
                </p>
                <p>
                  Time-based offers let you run happy hour specials, brunch promos, and late-night 
                  menu pushes on autopilot. Set the schedule once and TableChat handles the delivery 
                  based on what time the guest sits down.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Promotion Features</h4>
                <ul>
                  <li>Welcome messages with first-visit offers</li>
                  <li>Digital daily specials chalkboard</li>
                  <li>Time-based offers (happy hour, brunch, late-night)</li>
                  <li>Update specials in real time from the console</li>
                  <li>100% guest visibility — promos appear in active chat</li>
                </ul>
              </div>
            </div>

            {/* VIP & Loyalty */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>VIP Guest Data Capture</h3>
                <p>
                  TableChat prompts guests to join your VIP list during the meal — when they're happy, 
                  engaged, and most likely to say yes. Guests share their email or phone number, verified 
                  with a one-time code, directly in the chat. All data is captured with explicit opt-in consent.
                </p>
                <p>
                  VIP contact data flows directly into your management console and can be exported to 
                  marketing platforms for targeted outreach — building genuine loyalty through direct 
                  communication, not just a points program.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Loyalty Capabilities</h4>
                <ul>
                  <li>In-chat VIP enrollment during the meal</li>
                  <li>Consent-based data capture (email or phone — guest's choice)</li>
                  <li>Verified contact via one-time code confirmation</li>
                  <li>Restaurant and table context captured for segmentation</li>
                  <li>Data export for marketing platform sync</li>
                </ul>
              </div>
            </div>

            {/* Marketing Integrations */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Marketing Platform Integrations</h3>
                <p>
                  Guest data captured at the table syncs directly to Klaviyo, Mailchimp, Constant Contact, and more. 
                  Build segmented email lists, trigger automated welcome sequences, run win-back 
                  campaigns for lapsed guests, and send targeted promotions — all powered by first-party 
                  data you own.
                </p>
                <p>
                  Integrations are configured from the management console without developer involvement. 
                  Authenticate, select your data fields, and new guest enrollments sync automatically 
                  from that point forward.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Supported Platforms</h4>
                <ul>
                  <li>Klaviyo — email, SMS, and segmented flows</li>
                  <li>Mailchimp — audiences, automations, campaigns</li>
                  <li>Constant Contact — email campaigns, event promotions</li>
                  <li>Additional platforms supported on request</li>
                  <li>CSV export for additional platforms</li>
                  <li>No-code setup from the management console</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">$379</div>
              <p className="stat-label">Per month, per location — everything included</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">~1 Week</div>
              <p className="stat-label">From signup to fully operational</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">0</div>
              <p className="stat-label">Apps guests need to download</p>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Value */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>What Comes With Every Subscription</h2>
          <div className="subscription-grid">
            <div className="subscription-card">
              <div className="subscription-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" fill="#2196F3"/>
                  <path d="M7 9h10v2H7zm0-3h10v2H7z" fill="#2196F3"/>
                </svg>
              </div>
              <h4>Instant Guest-to-Staff Messaging</h4>
              <p>Guests connect instantly via NFC TableChat, reducing wait times and improving satisfaction.</p>
            </div>
            <div className="subscription-card">
              <div className="subscription-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#2196F3"/>
                </svg>
              </div>
              <h4>No App Required</h4>
              <p>Guests simply tap their phone to request service. Secure, reliable NFC technology works instantly.</p>
            </div>
            <div className="subscription-card">
              <div className="subscription-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="#2196F3"/>
                </svg>
              </div>
              <h4>Intuitive Staff Kiosk</h4>
              <p>Immediately determine which tables and guests have a request for something additional.</p>
            </div>
            <div className="subscription-card">
              <div className="subscription-icon">∞</div>
              <h4>Unlimited Staff Accounts</h4>
              <p>Add every server, manager, and host. No per-seat charges, no user caps.</p>
            </div>
            <div className="subscription-card">
              <div className="subscription-icon">📊</div>
              <h4>Daily Operational Reports</h4>
              <p>Every guest request and staff response from the past 24 hours, emailed to management daily.</p>
            </div>
            <div className="subscription-card">
              <div className="subscription-icon">🎨</div>
              <h4>Custom Branding</h4>
              <p>Your restaurant name, logo, and messaging — guests see your brand, not ours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ for AI Discoverability */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>Common Questions About TableChat</h2>
            <p>Answers to the questions restaurant owners and AI assistants ask most often.</p>
          </div>

          <div className="feature-blocks">
            {whyTableChatFAQs.map((faq, index) => (
              <div key={index} className="feature-block" style={{ gap: '24px' }}>
                <div className="feature-block-content" style={{ gridColumn: '1 / -1' }}>
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>See What TableChat Can Do for Your Restaurant</h2>
          <p>Simple setup, transparent pricing, no app for guests to download. Start in about a week.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default WhyTableChat;
